import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { viewTransition } from '@theme/utilities';

/**
 * Keep the quick-add copy limited to the original product media.
 * Run on detached response markup, before its native components are connected.
 * @param {Element} gallery
 */
export function prepareMediaGalleryForQuickAdd(gallery) {
  if (!gallery.hasAttribute('data-fedua-packshot-count')) return;

  gallery.querySelectorAll('[data-fedua-packshot]').forEach((element) => element.remove());

  // Quick-add already hides controls and disables zoom; remove their PDP-only indices.
  gallery.querySelectorAll('slideshow-controls, slideshow-arrows, zoom-dialog, .product-media-container__zoom-button')
    .forEach((element) => element.remove());

  const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
  const slides = slideshow?.querySelectorAll('slideshow-slide[ref="slides[]"]');
  slides?.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.removeAttribute('on:click');
    if (slide instanceof HTMLElement) slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
  });
  slideshow?.setAttribute('initial-slide', '0');
  if (slideshow instanceof HTMLElement && slides) {
    slideshow.style.setProperty('--slideshow-timeline', Array.from(slides, (_, index) => `--slide-${index}`).join(', '));
  }
  gallery.removeAttribute('data-fedua-packshot-count');
  gallery.removeAttribute('data-fedua-initial-slide');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-fedua-packshot-count')) this.#selectInitialMedia();
  }

  #controller = new AbortController();

  async #selectInitialMedia() {
    await customElements.whenDefined('slideshow-component');
    if (viewTransition.current) await viewTransition.current;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const index = Number(this.getAttribute('data-fedua-initial-slide'));
      if (!Number.isInteger(index) || index <= 0) return;
      // Changing the native attribute after setup selects and scrolls by media ID.
      this.slideshow?.setAttribute('initial-slide', String(index));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (this.closest('#quick-add-dialog')) {
          prepareMediaGalleryForQuickAdd(newMediaGallery);
        } else if (newMediaGallery.hasAttribute('data-fedua-packshot-count')) {
          const slideshow = newMediaGallery.querySelector('slideshow-component[ref="slideshow"]');
          const selectedMediaId = newMediaGallery.getAttribute('data-selected-media-id');
          const slides = slideshow?.querySelectorAll('slideshow-slide[ref="slides[]"]');
          const selectedIndex = slides
            ? Array.from(slides).findIndex((slide) => slide.getAttribute('slide-id') === selectedMediaId)
            : -1;
          const originalIndex = slides
            ? Array.from(slides).findIndex((slide) => !slide.hasAttribute('data-fedua-packshot'))
            : -1;
          const initialIndex = selectedIndex >= 0 ? selectedIndex : originalIndex;
          if (initialIndex >= 0) newMediaGallery.setAttribute('data-fedua-initial-slide', String(initialIndex));
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
